import { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import Papa from 'papaparse'

function DatasetPreviewModal({ dataset, isOpen, onClose, onDownload }) {
  const [previewData, setPreviewData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const loadPreviewData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await fetch(dataset.file_path)
      if (!response.ok) {
        throw new Error(`Failed to fetch dataset: ${response.statusText}`)
      }

      const csvText = await response.text()

      // Parse CSV with Papa Parse
      Papa.parse(csvText, {
        header: true,
        skipEmptyLines: true,
        transformHeader: header => header.trim(),
        complete: results => {
          if (results.errors.length > 0) {
            // CSV parsing warnings - continue with available data
          }

          // Get first 10 rows for preview
          const previewRows = results.data.slice(0, 10)
          const columns = Object.keys(previewRows[0] || {})

          setPreviewData({
            rows: previewRows,
            columns: columns,
            totalRows: results.data.length,
            totalColumns: columns.length,
          })
        },
        error: error => {
          throw new Error(`CSV parsing failed: ${error.message}`)
        },
      })
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [dataset])

  // Load preview data when modal opens
  useEffect(() => {
    if (isOpen && dataset) {
      loadPreviewData()
    } else {
      // Clear data when modal closes
      setPreviewData(null)
      setError(null)
    }
  }, [isOpen, dataset, loadPreviewData])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-7xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-surface-200">
          <div>
            <h2 className="text-xl font-semibold text-surface-900">
              Dataset Preview
            </h2>
            <p className="text-sm text-surface-600 mt-1">{dataset?.title}</p>
          </div>
          <button
            onClick={onClose}
            className="text-surface-400 hover:text-surface-600 transition-colors"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
                <p className="text-surface-600">Loading preview...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="text-red-600 text-4xl mb-4">⚠️</div>
                <h3 className="text-lg font-semibold text-red-800 mb-2">
                  Preview Failed
                </h3>
                <p className="text-red-700">{error}</p>
              </div>
            </div>
          ) : previewData ? (
            <>
              {/* Stats Summary */}
              <div className="px-6 py-4 bg-surface-50 border-b border-surface-200">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-surface-700">
                      Total Records:
                    </span>
                    <span className="ml-2 text-surface-900">
                      {previewData.totalRows.toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-surface-700">
                      Columns:
                    </span>
                    <span className="ml-2 text-surface-900">
                      {previewData.totalColumns}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-surface-700">
                      File Size:
                    </span>
                    <span className="ml-2 text-surface-900">
                      {dataset.file_size}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-surface-700">
                      Preview:
                    </span>
                    <span className="ml-2 text-surface-900">First 10 rows</span>
                  </div>
                </div>
              </div>

              {/* Data Table */}
              <div className="flex-1 overflow-auto p-6">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-surface-200">
                    <thead className="bg-surface-50">
                      <tr>
                        {previewData.columns.map((column, index) => (
                          <th
                            key={index}
                            className="px-3 py-3 text-left text-xs font-medium text-surface-700 uppercase tracking-wider whitespace-nowrap"
                          >
                            {column}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-surface-200">
                      {previewData.rows.map((row, rowIndex) => (
                        <tr key={rowIndex} className="hover:bg-surface-50">
                          {previewData.columns.map((column, colIndex) => (
                            <td
                              key={colIndex}
                              className="px-3 py-2 text-sm text-surface-900 whitespace-nowrap max-w-xs truncate"
                              title={row[column]}
                            >
                              {row[column] || '-'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : null}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-surface-200">
          <p className="text-sm text-surface-600">
            {previewData && previewData.totalRows > 10 ? (
              <>
                Showing first 10 of {previewData.totalRows.toLocaleString()}{' '}
                records
              </>
            ) : (
              <>Dataset preview</>
            )}
          </p>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-surface-300 rounded-lg font-medium text-surface-700 hover:bg-surface-50 transition-colors"
            >
              Close
            </button>
            <button
              onClick={() => {
                onDownload()
                onClose()
              }}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 transition-colors flex items-center space-x-2"
            >
              <span>⬇️</span>
              <span>Download Dataset</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

DatasetPreviewModal.propTypes = {
  dataset: PropTypes.shape({
    title: PropTypes.string,
    file_path: PropTypes.string,
    file_size: PropTypes.string,
  }),
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
}

export default DatasetPreviewModal
